import React, { useMemo, useState } from 'react'

import WidgetTable from './index'
import SegmentMenu from '../../SegmentMenu'
import { WidgetTableRow } from './hooks'

import { pathKey } from 'components_new/molecules/Table/utils'

import * as UserEvents from 'redux/api/internal/UserEvents'
import { Action } from 'types/GlobalUserEvents'

import {
  FormattedWidgetData,
  isDashboardWidgetFullyLoaded,
  ParsedSegmentPath,
  ParsedWidgetDataLabel,
  TCoreWidgetFullyLoaded,
  WidgetOrder
} from 'types/GlobalWidget'

interface DashboardWidgetTableProps {
  customSegmentBy: string | null
  embedded: boolean
  formattedData: FormattedWidgetData
  isComparativeButIllegal: boolean
  onClickExternalLink: ((link: string | null) => void) | null
  onSegmentClick?: (
    event: React.MouseEvent<HTMLElement, MouseEvent> | PointerEvent,
    segments: ParsedWidgetDataLabel[],
    datasetIndex?: number
  ) => void
  order: WidgetOrder
  scaleFactor: number
  setCustomSegmentBy:
    | ((attributeOptionId: string, segment: ParsedSegmentPath[]) => void)
    | null
  setDetailsDialog:
    | ((segment: ParsedSegmentPath[], kpiOptionId?: string) => void)
    | null
  setOrder: (order: WidgetOrder) => void
  setSegmentFilters: ((segment: ParsedSegmentPath[]) => void) | null
  widget: TCoreWidgetFullyLoaded
}

/**
 * Dashboard-specific wrapper for the WidgetTable component.
 *
 * This wrapper:
 * 1. Manages SegmentMenu state (anchor position, selected row)
 * 2. Handles cell click events to open SegmentMenu
 * 3. Handles sorting with UserEvents logging
 * 4. Passes dashboard_id and other dashboard-specific context
 */
const DashboardWidgetTable = (props: DashboardWidgetTableProps) => {
  const {
    customSegmentBy,
    embedded,
    formattedData,
    isComparativeButIllegal,
    onClickExternalLink,
    onSegmentClick,
    order,
    scaleFactor,
    setCustomSegmentBy,
    setDetailsDialog,
    setOrder,
    setSegmentFilters,
    widget
  } = props

  const [anchorPosition, setAnchorPosition] = useState<{
    top: number
    left: number
  } | null>(null)
  const [selectedRowData, setSelectedRowData] = useState<WidgetTableRow | null>(
    null
  )

  const labelByPath = useMemo(() => {
    const map = new Map<string, ParsedWidgetDataLabel>()

    formattedData.labels.forEach((label) => {
      const key = pathKey([
        ...label.path.map((p) => p.display_label),
        label.display_label
      ])

      map.set(key, label)
    })

    return map
  }, [formattedData.labels])

  // Cell click handler - opens SegmentMenu
  const handleCellClick = (
    row: WidgetTableRow,
    event: React.MouseEvent<HTMLElement>
  ) => {
    if (onSegmentClick) {
      const label = labelByPath.get(row.label.path)

      if (label) {
        onSegmentClick(event, [label])
      }

      return
    }

    event.stopPropagation()
    setAnchorPosition({ top: event.clientY, left: event.clientX })
    setSelectedRowData(row)
  }

  // Sort handler - logs UserEvents and updates order
  const handleSort = (id: string, index: number | null) => {
    const isAsc =
      order.id === id && order.index === index && order.dir === 'asc'

    setOrder({ id, index, dir: isAsc ? 'desc' : 'asc' })

    UserEvents.log({
      action: Action.LIST_SORTED,
      context: null,
      ...(isDashboardWidgetFullyLoaded(widget)
        ? { dashboard_id: widget.dashboard_id, dashboard_group_id: null }
        : // TODO: HP-14730
          { report_id: null })
    })
  }

  // Close SegmentMenu
  const handleCloseMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorPosition(null)
    setSelectedRowData(null)
  }

  return (
    <>
      <WidgetTable
        datasets={formattedData.datasets}
        formattedData={formattedData}
        isComparativeButIllegal={isComparativeButIllegal}
        onCellClick={handleCellClick}
        onSort={handleSort}
        order={order}
        scaleFactor={scaleFactor}
        settings={widget.settings}
        showLinks={!embedded}
      />
      {isDashboardWidgetFullyLoaded(widget) ? (
        <SegmentMenu
          anchorPosition={anchorPosition}
          customSegmentBy={customSegmentBy}
          dashboardId={widget.dashboard_id}
          kpiOptionId={selectedRowData?.kpi_option_id}
          link={selectedRowData?.link ?? null}
          onClickExternalLink={onClickExternalLink}
          onClose={handleCloseMenu}
          segments={selectedRowData?.label.segments ?? []}
          setCustomSegmentBy={setCustomSegmentBy}
          setDetailsDialog={
            setDetailsDialog
              ? (segments, kpiOptionId) =>
                  setDetailsDialog(segments, kpiOptionId ?? undefined)
              : null
          }
          setFilters={setSegmentFilters}
          widget={widget}
        />
      ) : null}
    </>
  )
}

export default DashboardWidgetTable
